import React, { useState } from 'react';
import api from '../services/api';

// Newsletter signup shown above the footer on every page.
export default function NewsletterSection() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setStatus('sending');
    try {
      await api.post('/api/newsletter', { email: email.trim() });
      setStatus('sent');
      setEmail('');
    } catch (err) {
      setStatus('error');
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    }
  };

  return (
    <section className="nl-section" aria-label="Newsletter signup">
      <div className="nl-inner">
        <div className="nl-text">
          <h2 className="nl-title">Stay in the loop</h2>
          <p className="nl-sub">
            Get the latest on XR, AI and immersive tech — straight to your inbox. No spam.
          </p>
        </div>

        {status === 'sent' ? (
          <div className="nl-success">✓ You’re subscribed. Welcome aboard!</div>
        ) : (
          <form className="nl-form" onSubmit={submit}>
            <input
              type="email"
              className="nl-input"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-label="Email address"
            />
            <button type="submit" className="nl-btn" disabled={status === 'sending'}>
              {status === 'sending' ? 'Subscribing…' : 'Subscribe'}
            </button>
          </form>
        )}
        {error && <p className="nl-error">{error}</p>}
      </div>

      <style>{`
        .nl-section {
          background: #050409;
          padding: var(--section-pad) 24px;
          border-top: 1px solid rgba(139,92,246,0.12);
          font-family: 'Poppins', 'Inter', system-ui, sans-serif;
        }
        .nl-inner {
          max-width: 1000px;
          margin: 0 auto;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: space-between;
          gap: 28px;
          padding: 40px;
          border-radius: 24px;
          background:
            radial-gradient(circle at 100% 0%, rgba(123,47,224,0.22), transparent 55%),
            linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02));
          border: 1px solid rgba(255,255,255,0.08);
        }
        .nl-text { flex: 1 1 300px; }
        /* A compact card title, so it keeps its own size; family, weight,
           tracking and colour follow the site heading style. */
        .nl-title {
          margin: 0 0 10px;
          font-family: var(--font-heading);
          font-size: clamp(24px, 4vw, 34px);
          font-weight: var(--type-h2-weight);
          line-height: var(--type-h2-line);
          letter-spacing: var(--type-h2-tracking);
          color: var(--type-text);
        }
        .nl-sub {
          margin: 0;
          font-family: var(--font-body);
          color: var(--type-body-color);
          font-size: var(--type-body-size);
          font-weight: var(--type-body-weight);
          line-height: var(--type-body-line);
          max-width: 420px;
        }
        .nl-form {
          flex: 1 1 360px;
          display: flex;
          gap: 12px;
          max-width: 460px;
        }
        .nl-input {
          flex: 1;
          padding: 14px 18px;
          border-radius: 100px;
          border: 1px solid rgba(255,255,255,0.14);
          background: rgba(0,0,0,0.35);
          color: #fff;
          font-size: 15px;
          font-family: inherit;
        }
        .nl-input:focus { outline: none; border-color: rgba(155,77,255,0.6); }
        .nl-btn {
          padding: 14px 28px;
          border: none;
          border-radius: 100px;
          background: #7B2FE0;
          color: #fff;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          white-space: nowrap;
          transition: background .25s, transform .25s;
        }
        .nl-btn:hover:not(:disabled) { background: #9B4DFF; transform: translateY(-2px); }
        .nl-btn:disabled { opacity: 0.6; cursor: default; }
        .nl-success {
          flex: 1 1 360px;
          max-width: 460px;
          padding: 16px 22px;
          border-radius: 14px;
          background: rgba(74,222,128,0.12);
          border: 1px solid rgba(74,222,128,0.3);
          color: #7ef0a8;
          font-weight: 600;
        }
        .nl-error {
          flex-basis: 100%;
          margin: 4px 0 0;
          color: #ff8095;
          font-size: 13px;
          text-align: right;
        }
        @media (max-width: 720px) {
          .nl-inner { padding: 28px; }
          .nl-form { flex-direction: column; max-width: 100%; }
          .nl-error { text-align: left; }
        }
      `}</style>
    </section>
  );
}
